import { UntitledIcon } from '@faceup/icons'
import { ulMessageTextSquare02 } from '@faceup/icons/ulMessageTextSquare02'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { Divider, Typography } from '@faceup/ui-base'
import { CaseCreationOrigin, compact } from '@faceup/utils'
import { useDocumentVisibility } from '@mantine/hooks'
import { useCallback, useContext, useDeferredValue, useEffect, useMemo, useRef } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import FollowUpChatMessage from './FollowUpChatMessage'
import { RelativeMessagesContext } from './RelativeMessagesContext'

const { Text } = Typography

const messages = defineMessages('report.components.chat', [
  { id: 'newMessage', defaultMessage: '' },
] as const)

const fragments = {
  Chat_case: graphql(`
    fragment Chat_case on CaseInterface {
      creationOrigin
      isDemo
      victim {
        id
      }
    }
  `),
}

type FollowUpActivity = React.ComponentProps<typeof FollowUpChatMessage>['message'] & {
  author?: { id: string } | null
  id: string
}

type Props = {
  report: FragmentType<typeof fragments.Chat_case> | null
  followUpActivities: FollowUpActivity[]
  subscribeToNewMessages?: () => (() => void) | null
  onMessageSeen?: () => void
  userId: string
  fileDownloadHandler?: (blob: Blob, name: string) => Promise<void>
  firstUnreadMessageId?: string
  newestMessagesLoadedFirst?: boolean
}

const Chat = ({
  report: _report,
  followUpActivities: _followUpActivities,
  userId,
  fileDownloadHandler,
  onMessageSeen,
  firstUnreadMessageId,
  newestMessagesLoadedFirst = false,
}: Props) => {
  const report = getFragmentData(fragments.Chat_case, _report)
  const relativeMessages = useContext(RelativeMessagesContext)
  const followUpActivities = useMemo(
    () => (newestMessagesLoadedFirst ? [..._followUpActivities].reverse() : _followUpActivities),
    [_followUpActivities, newestMessagesLoadedFirst]
  )
  // Each message suspends while it decrypts. Deferring the list keeps the
  // already-committed (decrypted) messages on screen while a newly arrived or
  // paged-in message resolves in the background, instead of the whole chat
  // dropping to the Suspense fallback. On first render deferred === actual, so
  // export mode still suspends once until everything is decrypted.
  const deferredActivities = useDeferredValue(followUpActivities)
  const scrollTargetRef = useRef<HTMLDivElement | null>(null)

  const documentState = useDocumentVisibility()

  const scrollToNewestMessage = useCallback(() => {
    scrollTargetRef.current?.scrollIntoView({
      // for some reason, 'smooth' behavior was erratic (would scroll on some occasions and not on others)
      behavior: 'auto',
      // nearest will only scroll inner container; "center" will scroll the whole page
      block: 'nearest',
    })
  }, [])

  // Re-focus the unread divider when the user returns to the tab. The 700ms
  // delay gives the async decryption pipeline time to populate message DOM
  // (so scrolling targets the final position, not an intermediate height).
  // It's a heuristic — too short on slow machines, slightly wasteful on fast
  // ones — but cheap enough that a proper "decrypted" signal isn't worth the
  // plumbing yet.
  // biome-ignore lint/correctness/useExhaustiveDependencies(scrollToNewestMessage): stable
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (scrollTargetRef.current && documentState === 'visible') {
        scrollToNewestMessage()
      }
    }, 700)
    return () => clearTimeout(timeoutId)
  }, [documentState])

  return (
    <>
      {deferredActivities.length === 0 && (
        <div className='flex items-center justify-center py-6'>
          <div className='flex flex-col items-center gap-4'>
            <div className='flex items-center justify-center rounded-full size-14 bg-primary1'>
              <UntitledIcon icon={ulMessageTextSquare02} size={20} className='text-primary5' />
            </div>
            <Text className='text-colorTextDescription'>
              <FormattedMessage message={relativeMessages.empty} />
            </Text>
          </div>
        </div>
      )}
      {((report?.creationOrigin &&
        [CaseCreationOrigin.Reporter, CaseCreationOrigin.Redaction].includes(
          report.creationOrigin
        )) ||
        report?.isDemo) &&
        compact(deferredActivities).map(node => (
          <div key={node?.id} className='w-full' data-follow-up-message-id={node.id}>
            {node?.id === firstUnreadMessageId && (
              <div ref={scrollTargetRef} style={{ marginInlineEnd: '12px' }}>
                <NewMessageDivider />
              </div>
            )}
            <FollowUpChatMessage
              onMessageSeen={onMessageSeen}
              orientation={node?.author?.id === userId ? 'right' : 'left'}
              key={node?.id}
              message={node}
              fileDownloadHandler={fileDownloadHandler}
            />
          </div>
        ))}
    </>
  )
}

export const NewMessageDivider = ({ style }: { style?: React.CSSProperties }) => {
  return (
    <Divider
      // keeping `style` so I don't have to override styles with !important
      style={{ ...style, borderColor: '#E42457', color: '#E42457', fontSize: '12px' }}
      orientation='right'
      orientationMargin='0'
    >
      <FormattedMessage message={messages.newMessage} />
    </Divider>
  )
}

export default Chat
